import React, { useState } from 'react';

import { Pencil, Trash2 } from 'lucide-react';
import ReactModal from 'react-modal';

import { Macro } from '../types';

type Props = {
  isOpen: boolean;
  macros: Macro[];
  onClose: () => void;
  onCreate: (title: string, body: string) => Promise<void>;
  onUpdate: (id: string, title: string, body: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
};

/**
 * The CRUD behind the "/" picker — telescope keeps this in a modal too
 * (QuickResponseModal). One form serves create and edit; the list sits above it.
 */
const MacroModal: React.FC<Props> = ({
  isOpen,
  macros,
  onClose,
  onCreate,
  onUpdate,
  onDelete,
}: Props) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const reset = (): void => {
    setEditingId(null);
    setTitle('');
    setBody('');
  };

  const save = async (): Promise<void> => {
    if (!title.trim() || !body.trim() || isSaving) return;
    setIsSaving(true);
    try {
      if (editingId) await onUpdate(editingId, title.trim(), body.trim());
      else await onCreate(title.trim(), body.trim());
      reset();
    } catch {
      // Keep what they typed: the caller already told them the save failed,
      // and a cleared form on top of that error loses the only copy.
    } finally {
      setIsSaving(false);
    }
  };

  const remove = (macro: Macro): void => {
    // Shared by the whole team and one click away from the edit pencil — a
    // slip here deletes it for everyone, with no undo.
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${macro.title}" for the whole team?`)) return;
    void onDelete(macro.id).catch(() => undefined);
  };

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="modal"
      overlayClassName="overlay"
      appElement={document.querySelector('#root') as HTMLElement}
    >
      <h2 className="text-lg font-semibold text-black-1.0">Quick replies</h2>
      <p className="mt-1 text-sm text-gray-1.0">
        Shared by the whole team. Type / in the composer to use one.
      </p>

      <ul className="mt-4 overflow-y-auto max-h-80 divide-y divide-white-1.0">
        {macros.length === 0 && <li className="py-2 text-sm text-gray-1.0">None yet.</li>}
        {macros.map((macro) => (
          <li key={macro.id} className="flex items-center gap-2 py-2">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate text-black-1.0">{macro.title}</p>
              <p className="text-sm truncate text-gray-1.0">{macro.body}</p>
            </div>
            <button
              type="button"
              aria-label={`Edit ${macro.title}`}
              className="p-1 rounded text-gray-2.0 hover:bg-white-1.0"
              onClick={() => {
                setEditingId(macro.id);
                setTitle(macro.title);
                setBody(macro.body);
              }}
            >
              <Pencil size={16} />
            </button>
            <button
              type="button"
              aria-label={`Delete ${macro.title}`}
              className="p-1 rounded text-red-1.0 hover:bg-white-1.0"
              onClick={() => remove(macro)}
            >
              <Trash2 size={16} />
            </button>
          </li>
        ))}
      </ul>

      <div className="pt-4 mt-4 border-t border-white-2.0">
        <p className="text-sm font-medium text-black-1.0">
          {editingId ? 'Edit quick reply' : 'New quick reply'}
        </p>
        <input
          type="text"
          value={title}
          maxLength={120}
          onChange={(event) => setTitle(event.target.value)}
          placeholder="Title (what you search by)"
          className="w-full px-3 py-2 mt-2 border rounded border-white-3.0 focus:outline-none"
        />
        <textarea
          rows={3}
          value={body}
          onChange={(event) => setBody(event.target.value)}
          placeholder="Message to insert"
          className="w-full px-3 py-2 mt-2 border rounded resize-none border-white-3.0 focus:outline-none"
        />
        <div className="flex justify-end gap-2 mt-3">
          {editingId && (
            <button
              type="button"
              className="px-3 py-1 text-sm rounded text-gray-2.0 hover:bg-white-1.0"
              onClick={reset}
            >
              Cancel edit
            </button>
          )}
          <button
            type="button"
            className="px-3 py-1 text-sm rounded text-gray-2.0 hover:bg-white-1.0"
            onClick={onClose}
          >
            Close
          </button>
          <button
            type="button"
            disabled={!title.trim() || !body.trim() || isSaving}
            className="px-4 py-1 text-sm rounded bg-primary-2.0 text-white-0.0 disabled:opacity-50"
            onClick={() => void save()}
          >
            {editingId ? 'Save changes' : 'Add'}
          </button>
        </div>
      </div>
    </ReactModal>
  );
};

export default MacroModal;
